import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { Widget } from "components/elements/mui";
import { useFormatMessage } from "lang";
import { getLiveData, getSelectedPowerPlant, useAppSelector } from "store";
import {
  calculateLVCurrent,
  calculateLVVoltage,
  calculateMVCurrent,
  calculateMVVoltage,
} from "utils/energy";

export const AdditionalReadings = () => {
  const formatMessage = useFormatMessage();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const liveData = useAppSelector(getLiveData);

  if (!liveData || !selectedPowerPlant) return null;

  // These are meter readings: the utility meter reports the CT-secondary
  // current and transformer-secondary voltage, which we scale to MV/LV.
  // When the live snapshot is inverter-sourced there is no meter feeding
  // this, so the whole widget has nothing meaningful to show — hide it.
  if (liveData.source === "inverter") return null;

  const readings: {
    name: "mv.voltage" | "mv.current" | "lv.voltage" | "lv.current";
    R: () => number;
    S: () => number;
    T: () => number;
    unit: string;
    hide: boolean;
  }[] = [
    {
      name: "mv.voltage",
      R: () =>
        calculateMVVoltage(
          liveData.voltage.L1,
          selectedPowerPlant.mediumVoltage!,
        ),
      S: () =>
        calculateMVVoltage(
          liveData.voltage.L2,
          selectedPowerPlant.mediumVoltage!,
        ),
      T: () =>
        calculateMVVoltage(
          liveData.voltage.L3,
          selectedPowerPlant.mediumVoltage!,
        ),
      unit: "kV",
      hide: !selectedPowerPlant.mediumVoltage,
    },
    {
      name: "mv.current",
      R: () =>
        calculateMVCurrent(
          liveData.current.L1,
          selectedPowerPlant.mediumCurrent,
        ),
      S: () =>
        calculateMVCurrent(
          liveData.current.L2,
          selectedPowerPlant.mediumCurrent,
        ),
      T: () =>
        calculateMVCurrent(
          liveData.current.L3,
          selectedPowerPlant.mediumCurrent,
        ),
      unit: "A",
      hide: !selectedPowerPlant.mediumVoltage,
    },
    {
      name: "lv.voltage",
      R: () =>
        calculateLVVoltage(
          liveData.voltage.L1,
          selectedPowerPlant.mediumVoltage,
        ),
      S: () =>
        calculateLVVoltage(
          liveData.voltage.L2,
          selectedPowerPlant.mediumVoltage,
        ),
      T: () =>
        calculateLVVoltage(
          liveData.voltage.L3,
          selectedPowerPlant.mediumVoltage,
        ),
      unit: "V",
      hide: false,
    },
    {
      name: "lv.current",
      R: () =>
        calculateLVCurrent(
          liveData.current.L1,
          selectedPowerPlant.mediumCurrent,
          selectedPowerPlant.mediumVoltage,
        ),
      S: () =>
        calculateLVCurrent(
          liveData.current.L2,
          selectedPowerPlant.mediumCurrent,
          selectedPowerPlant.mediumVoltage,
        ),
      T: () =>
        calculateLVCurrent(
          liveData.current.L3,
          selectedPowerPlant.mediumCurrent,
          selectedPowerPlant.mediumVoltage,
        ),
      unit: "A",
      hide: false,
    },
  ];

  return (
    <Widget
      title={formatMessage("meter.readings")}
      icon="note_add"
      size={{ xs: 12 }}
      content={
        <TableContainer component={Box}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="center">R</TableCell>
                <TableCell align="center">S</TableCell>
                <TableCell align="center">T</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {readings
                .filter((reading) => !reading.hide)
                .map((reading) => (
                  <TableRow
                    key={reading.name}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell key={reading.name} align="center">
                      {`${formatMessage(reading.name)} (${reading.unit})`}
                    </TableCell>
                    <TableCell align="center">
                      {selectedPowerPlant.offline ? "—" : reading.R()}
                    </TableCell>
                    <TableCell align="center">
                      {selectedPowerPlant.offline ? "—" : reading.S()}
                    </TableCell>
                    <TableCell align="center">
                      {selectedPowerPlant.offline ? "—" : reading.T()}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      }
    />
  );
};
